import type { ReactNode } from 'react';
import React, { createContext, useEffect, useReducer } from 'react';
import { isAxiosError } from 'axios';
import axios from '../common/axios';
import { isValidToken, setSession } from '../common/jwt';
// ─────────────────────────────────────────────────────────────────────────────
import { setUser, clearUser } from '../common/sentry';
import { logEvent } from '../experimentation/events';
import { dispatch as reduxDispatch } from '../redux/store';
import { getMemberships, setActiveOrganization } from '../features/organization-context/api';
import { linkPendingInviteAfterAuth } from '../pages/invitation/api';
// ─── Auth types ───────────────────────────────────────────────────────────────
type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined ? { type: Key } : { type: Key; payload: M[Key] };
};

type AuthUser = null | Record<string, any>;

type AuthState = {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser;
};

export type JWTContextType = {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser;
  method: 'jwt';
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  newPassword: (
    email: string,
    code: string,
    password: string,
    confirmPassword: string
  ) => Promise<boolean>;
  updateProfile: (
    email: string,
    firstName: string,
    lastName: string,
    displayName: string,
    about: string,
    language: string,
    themeMode: number
  ) => Promise<void>;
  updatePhotoUrl: (email: string, photoURL: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  changeEmail: (newEmail: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  verify: (email: string, code: string) => Promise<boolean>;
  resendVerificationCode: (email: string) => Promise<boolean>;
};

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
  Update = 'UPDATE'
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
  [Types.Update]: {
    user: AuthUser;
  };
};

type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: false,
        user: action.payload.user
      };

    case 'UPDATE':
      return {
        ...state,
        user: action.payload.user
      };

    /* c8 ignore next */
    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const refreshToken = window.localStorage.getItem('refreshToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
        } else if (refreshToken) {
          const { data } = await axios.post('user/Refresh', { refreshToken });
          setSession(data.accessToken, data.refreshToken);
        } else {
          setSession(null);
          dispatch({ type: Types.Initial, payload: { isAuthenticated: false, user: null } });
          clearUser();
          return;
        }

        const response = await axios.get('user/account');
        const { user } = response.data;

        dispatch({ type: Types.Initial, payload: { isAuthenticated: true, user } });
        reduxDispatch(getMemberships());
        setUser({
          id: user.guid,
          email: user.email,
          username: user.displayName || `${user.firstName} ${user.lastName}`
        });
        logEvent('user_session_resumed', { user_id: user.guid, role: String(user.role ?? '') });
      } catch (error) {
        // A network error (no response received) doesn't mean the refresh
        // token is invalid — keep the session so the next reload can retry.
        if (isAxiosError(error) && !error.response) {
          dispatch({ type: Types.Initial, payload: { isAuthenticated: false, user: null } });
          return;
        }

        setSession(null);
        dispatch({ type: Types.Initial, payload: { isAuthenticated: false, user: null } });
        clearUser();
      }
    };

    initialize();
  }, []);

  const login = async (email: string, password: string) => {
    await axios.post('user/authenticate', { email, password }).then((response) => {
      const { accessToken, refreshToken, user } = response.data;
      setSession(accessToken, refreshToken);
      dispatch({
        type: Types.Login,
        payload: {
          user
        }
      });
      reduxDispatch(getMemberships());

      // Set user context in Sentry
      setUser({
        id: user.guid,
        email: user.email,
        username: user.displayName || `${user.firstName} ${user.lastName}`
      });
      logEvent('user_signed_in', { user_id: user.guid, role: String(user.role ?? '') });
      linkPendingInviteAfterAuth();
    });
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: string
  ) => {
    const response = await axios.post('user/register', {
      email,
      password,
      firstName,
      lastName,
      registeredRole: parseInt(role, 10)
    });
    const { user } = response.data;

    dispatch({
      type: Types.Register,
      payload: {
        user
      }
    });

    setUser({
      id: user.guid,
      email: user.email,
      username: user.displayName || `${user.firstName} ${user.lastName}`
    });
    linkPendingInviteAfterAuth();
  };

  const verify = async (email: string, code: string): Promise<boolean> => {
    const response = await axios.post('user/verifyAccount', {
      email,
      code
    });

    const { isValidCode, accessToken, refreshToken, user } = response.data;

    if (isValidCode && accessToken) {
      setSession(accessToken, refreshToken);
      dispatch({
        type: Types.Login,
        payload: {
          user
        }
      });
      reduxDispatch(getMemberships());

      setUser({
        id: user.guid,
        email: user.email,
        username: user.displayName || `${user.firstName} ${user.lastName}`
      });
      linkPendingInviteAfterAuth();
    }

    return isValidCode;
  };

  const resendVerificationCode = async (email: string): Promise<boolean> => {
    const response = await axios.post('user/resendVerificationCode', {
      email
    });

    return response.data;
  };

  const logout = async () => {
    const refreshToken = window.localStorage.getItem('refreshToken');
    setSession(null);
    dispatch({ type: Types.Logout });
    reduxDispatch(setActiveOrganization(null));
    clearUser();

    if (refreshToken) {
      axios.post('user/Logout', { refreshToken }).catch(() => {});
    }
  };

  const resetPassword = async (email: string) => {
    await axios.post('user/forgotPassword', {
      email
    });
  };

  const newPassword = async (
    email: string,
    code: string,
    password: string,
    confirmPassword: string
  ) => {
    const response = await axios.post(`user/newPassword/${email}`, {
      code,
      password,
      confirmPassword
    });

    return response.data;
  };

  const updateProfile = async (
    email: string,
    firstName: string,
    lastName: string,
    displayName: string,
    about: string,
    language: string,
    themeMode: number
  ) => {
    const response = await axios.put('user/update', {
      email,
      firstName,
      lastName,
      displayName,
      about,
      language,
      themeMode
    });
    // API returns the user model as the JSON root (not wrapped in { user }).
    const user = response.data as AuthUser;
    if (user == null) {
      return;
    }

    dispatch({
      type: Types.Update,
      payload: {
        user
      }
    });

    // Update user context in Sentry
    setUser({
      id: user.guid,
      email: user.email,
      username: user.displayName || `${user.firstName} ${user.lastName}`
    });
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    await axios.post('user/changePassword', { oldPassword, newPassword });
  };

  const changeEmail = async (newEmail: string) => {
    const response = await axios.put('user/changeEmail', { newEmail });
    const { user } = response.data;

    dispatch({
      type: Types.Update,
      payload: { user }
    });

    setUser({
      id: user.guid,
      email: user.email,
      username: user.displayName || `${user.firstName} ${user.lastName}`
    });
  };

  const deleteAccount = async () => {
    await axios.delete('user/account');
    setSession(null);
    dispatch({ type: Types.Logout });
    clearUser();
  };

  const updatePhotoUrl = async (email: string, photoUrl: string) => {
    const response = await axios.post('user/UpdatePhotoUrl', {
      email,
      photoUrl
    });
    const user = response.data as AuthUser;
    if (user == null) {
      return;
    }

    dispatch({
      type: Types.Update,
      payload: {
        user
      }
    });

    // Update user context in Sentry
    setUser({
      id: user.guid,
      email: user.email,
      username: user.displayName || `${user.firstName} ${user.lastName}`
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        newPassword,
        register,
        resetPassword,
        updateProfile,
        updatePhotoUrl,
        changePassword,
        changeEmail,
        deleteAccount,
        verify,
        resendVerificationCode
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
